import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { apiEndpoints } from '../config/api';

const detailsEndpoint = apiEndpoints.propertyDetails;
const enquiryEndpoint = apiEndpoints.propertyEnquiry;
const fallbackImage = '/assets/img/property-banner.jpg';

const cleanText = (value) => {
  if (!value) return '';
  const documentNode = new DOMParser().parseFromString(String(value), 'text/html');
  return (documentNode.body.textContent || '').replace(/\s+/g, ' ').trim();
};

const sanitizeRichHtml = (value) => {
  if (!value) return '';
  const documentNode = new DOMParser().parseFromString(String(value), 'text/html');
  const allowedTags = new Set([
    'P', 'BR', 'H2', 'H3', 'H4', 'UL', 'OL', 'LI',
    'STRONG', 'B', 'EM', 'I', 'U', 'BLOCKQUOTE', 'A'
  ]);

  documentNode.body.querySelectorAll('script, style, iframe, object, embed, form, input, template').forEach((element) => {
    element.remove();
  });

  Array.from(documentNode.body.querySelectorAll('*')).forEach((element) => {
    if (element.tagName === 'H1') {
      const heading = documentNode.createElement('h2');
      heading.innerHTML = element.innerHTML;
      element.replaceWith(heading);
      return;
    }

    if (!allowedTags.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      return;
    }

    Array.from(element.attributes).forEach((attribute) => {
      if (element.tagName !== 'A' || !['href', 'title'].includes(attribute.name.toLowerCase())) {
        element.removeAttribute(attribute.name);
      }
    });

    if (element.tagName === 'A') {
      const href = element.getAttribute('href') || '';
      if (!/^(https?:|mailto:|tel:|\/|#)/i.test(href)) {
        element.removeAttribute('href');
      } else if (/^https?:/i.test(href)) {
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noopener noreferrer');
      }
    }
  });

  return documentNode.body.innerHTML;
};

const firstValue = (...values) => values.find((value) => String(value || '').trim()) || '';

const formatPrice = (value) => {
  const number = Number.parseFloat(String(value || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(number) && number > 0
    ? `AED ${Math.round(number).toLocaleString('en-US')}`
    : 'Price on request';
};

const splitItems = (value) => {
  if (!value) return [];
  const documentNode = new DOMParser().parseFromString(String(value), 'text/html');
  const listItems = Array.from(documentNode.querySelectorAll('li'))
    .map((item) => cleanText(item.textContent))
    .filter(Boolean);
  if (listItems.length) return listItems;
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(cleanText).filter(Boolean);
  } catch {
    // The feed commonly supplies amenities as comma-separated text.
  }
  return String(value)
    .split(/[\n,;|]+/)
    .map((item) => cleanText(item))
    .filter(Boolean);
};

const displayMetric = (value) => {
  const text = String(value || '').trim();
  if (!text || text === '0' || text === '-1' || text.toLowerCase() === 'null') return '';
  return text;
};

const setMeta = (selector, attribute, value) => {
  let element = document.head.querySelector(selector);
  if (!value) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement(attribute === 'rel' ? 'link' : 'meta');
    document.head.appendChild(element);
  }
  if (attribute === 'rel') {
    element.setAttribute('rel', 'canonical');
    element.setAttribute('href', value);
  } else {
    element.setAttribute(attribute, selector.includes('property=') ? selector.match(/property="([^"]+)/)?.[1] : selector.match(/name="([^"]+)/)?.[1]);
    element.setAttribute('content', value);
  }
};

const PropertyDetails = () => {
  const { seoSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [floorIndex, setFloorIndex] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [lightboxScale, setLightboxScale] = useState(1);
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '', website: '' });
  const [formState, setFormState] = useState({ submitting: false, type: '', message: '' });

  useEffect(() => {
    document.body.classList.add('elevate-home', 'property-detail-page');
    window.scrollTo({ top: 0, behavior: 'auto' });
    return () => document.body.classList.remove('elevate-home', 'property-detail-page');
  }, [seoSlug]);

  useEffect(() => {
    const controller = new AbortController();
    const loadProperty = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${detailsEndpoint}?seo_slug=${encodeURIComponent(seoSlug || '')}`,
          { signal: controller.signal }
        );
        const payload = await response.json();
        if (!response.ok || payload.result !== 1) {
          throw new Error(payload.message || 'Property details could not be loaded.');
        }
        setData(payload.data);
        setFloorIndex(0);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError(requestError.message || 'Property details could not be loaded.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    loadProperty();
    return () => controller.abort();
  }, [seoSlug]);

  const property = useMemo(() => data?.property || {}, [data]);
  const seo = useMemo(() => data?.seo || {}, [data]);
  const photos = useMemo(() => {
    const list = (data?.photos || []).filter((photo) => photo.url);
    if (!list.length && property.ImagePath) {
      return [{ id: 'featured', url: property.ImagePath, name: property.title }];
    }
    return list.map((photo, index) => ({
      ...photo,
      name: photo.name && !/^https?:\/\//i.test(photo.name)
        ? photo.name
        : `${property.title || 'Property image'} ${index + 1}`
    }));
  }, [data, property.ImagePath, property.title]);
  const floorPlans = data?.floorPlans || [];
  const paymentPlans = data?.paymentPlans || [];
  const heroImage = firstValue(property.ImagePath, photos[0]?.url, fallbackImage);
  const altText = firstValue(seo.image_alt, property.title, 'Elevate Realty property');
  const rawDescription = firstValue(
    seo.property_description_new,
    property.description,
    seo.ai_answer,
    seo.short_description
  );
  const description = cleanText(rawDescription);
  const descriptionHtml = useMemo(() => sanitizeRichHtml(rawDescription), [rawDescription]);
  const summary = cleanText(firstValue(seo.ai_summary, seo.short_description, description));
  const amenities = useMemo(() => splitItems(property.amenities).slice(0, 16), [property.amenities]);
  const displaySize = displayMetric(property.size)
    || [displayMetric(property.minsize), displayMetric(property.maxsize)].filter(Boolean).join(' – ');
  const displayBedrooms = displayMetric(property.bedrooms)
    || [displayMetric(property.minbed), displayMetric(property.maxbed)].filter(Boolean).join(' – ');
  const displayPrice = property.price || property.minprice;

  useEffect(() => {
    if (!data) return undefined;
    const title = firstValue(seo.seo_title, `${property.title} | Elevate Realty`);
    const metaDescription = firstValue(seo.meta_description, seo.short_description, description).slice(0, 165);
    const canonical = firstValue(seo.canonical_url, window.location.href.split('?')[0]);

    document.title = title;
    setMeta('meta[name="description"]', 'name', metaDescription);
    setMeta('meta[name="robots"]', 'name', firstValue(seo.robots, 'index, follow'));
    setMeta('meta[property="og:title"]', 'property', firstValue(seo.og_title, title));
    setMeta('meta[property="og:description"]', 'property', firstValue(seo.og_description, metaDescription));
    setMeta('meta[property="og:image"]', 'property', heroImage);
    setMeta('meta[name="twitter:title"]', 'name', firstValue(seo.twitter_title, title));
    setMeta('meta[name="twitter:description"]', 'name', firstValue(seo.twitter_description, metaDescription));
    setMeta('link[rel="canonical"]', 'rel', canonical);

    let schemaNode;
    if (seo.schema_json) {
      try {
        JSON.parse(seo.schema_json);
        schemaNode = document.createElement('script');
        schemaNode.type = 'application/ld+json';
        schemaNode.dataset.propertySchema = 'true';
        schemaNode.textContent = seo.schema_json;
        document.head.appendChild(schemaNode);
      } catch {
        // Invalid saved schema is ignored instead of breaking the page.
      }
    }
    return () => schemaNode?.remove();
  }, [data, description, heroImage, property.title, seo]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const close = (event) => {
      if (event.key === 'Escape') setLightbox(null);
      if (event.key === 'ArrowLeft') {
        setLightbox((current) => ({
          ...current,
          index: (current.index - 1 + current.items.length) % current.items.length
        }));
        setLightboxScale(1);
      }
      if (event.key === 'ArrowRight') {
        setLightbox((current) => ({
          ...current,
          index: (current.index + 1) % current.items.length
        }));
        setLightboxScale(1);
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', close);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', close);
    };
  }, [lightbox]);

  const openLightbox = (items, index = 0) => {
    const availableItems = items.filter((item) => item?.url);
    if (!availableItems.length) return;
    setLightbox({ items: availableItems, index: Math.min(index, availableItems.length - 1) });
    setLightboxScale(1);
  };

  const moveLightbox = (direction) => {
    setLightbox((current) => ({
      ...current,
      index: (current.index + direction + current.items.length) % current.items.length
    }));
    setLightboxScale(1);
  };

  const shareCurrentImage = async () => {
    const item = lightbox?.items[lightbox.index];
    if (!item) return;
    const shareData = { title: item.name || property.title, url: item.url };
    if (navigator.share) {
      await navigator.share(shareData).catch(() => {});
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(item.url).catch(() => {});
    }
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      document.querySelector('.detail-react-lightbox')?.requestFullscreen?.();
    }
  };

  const submitEnquiry = async (event) => {
    event.preventDefault();
    setFormState({ submitting: true, type: '', message: '' });
    try {
      const response = await fetch(enquiryEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          source: 'Property Detail Page',
          propertyType: property.property_type,
          propertyTitle: property.title,
          referenceNumber: property.reference_number,
          pageUrl: window.location.href
        })
      });
      const payload = await response.json();
      if (!response.ok || payload.result !== 1) {
        throw new Error(payload.message || 'We could not send your enquiry.');
      }
      setForm({ name: '', phone: '', email: '', message: '', website: '' });
      setFormState({
        submitting: false,
        type: 'success',
        message: 'Thank you. Your property enquiry has been sent to our advisory team.'
      });
    } catch (submissionError) {
      setFormState({
        submitting: false,
        type: 'error',
        message: submissionError.message || 'We could not send your enquiry. Please try again.'
      });
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <main className="detail-page detail-state-page" aria-live="polite">
          <div className="detail-loading" />
          <p>Loading property details…</p>
        </main>
        <Footer />
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Header />
        <main className="detail-page detail-state-page">
          <p className="detail-eyebrow">Property unavailable</p>
          <h1>We could not load this property.</h1>
          <p>{error}</p>
          <Link className="detail-button is-filled" to="/new-projects">Back to properties</Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="detail-page" id="top">
        <section
          className="detail-hero"
          style={{ '--detail-hero-image': `url("${heroImage.replace(/"/g, '%22')}")` }}
        >
          <div className="detail-hero-main">
            <div className="detail-hero-copy">
              <p className="detail-eyebrow">{firstValue(property.developer, property.community)}</p>
              <h1>{property.title}</h1>
              <p>{summary}</p>
              <div className="detail-actions">
                <a className="detail-button is-filled" href="#register">Request details</a>
                <a className="detail-button" href={`https://wa.me/971507135432?text=${encodeURIComponent(`I am interested in ${property.title}`)}`}>
                  WhatsApp us
                </a>
              </div>
            </div>

            <div className="detail-hero-stats" aria-label="Property quick facts">
              <div><strong>{formatPrice(displayPrice)}</strong><span>Starting price</span></div>
              <div><strong>{displayBedrooms || 'Ask us'}</strong><span>Bedrooms</span></div>
              <div><strong>{displaySize || 'Ask us'}</strong><span>{property.sizeunits || 'Size'}</span></div>
              <div><strong>{property.property_type || 'Property'}</strong><span>Unit type</span></div>
            </div>
          </div>

          <aside className="detail-lead-card" id="register">
            <p className="detail-eyebrow">Private consultation</p>
            <h2>Register interest</h2>
            <form className="detail-form" onSubmit={submitEnquiry}>
              <label className="detail-form-honeypot" aria-hidden="true">
                <span>Website</span>
                <input type="text" value={form.website} tabIndex={-1} autoComplete="off" onChange={(event) => setForm({ ...form, website: event.target.value })} />
              </label>
              <label>
                <span>Full name</span>
                <input required type="text" value={form.name} placeholder="Your name" autoComplete="name" onChange={(event) => setForm({ ...form, name: event.target.value })} />
              </label>
              <label>
                <span>Phone</span>
                <input required type="tel" value={form.phone} placeholder="+971 50 ..." autoComplete="tel" onChange={(event) => setForm({ ...form, phone: event.target.value })} />
              </label>
              <label>
                <span>Email</span>
                <input required type="email" value={form.email} placeholder="you@example.com" autoComplete="email" onChange={(event) => setForm({ ...form, email: event.target.value })} />
              </label>
              <label>
                <span>Message</span>
                <textarea value={form.message} placeholder="Budget, timeline or questions..." onChange={(event) => setForm({ ...form, message: event.target.value })} />
              </label>
              <button className="detail-button is-filled" type="submit" disabled={formState.submitting}>
                {formState.submitting ? 'Sending…' : 'Send enquiry'}
              </button>
              {formState.message && (
                <p className={`detail-form-status is-${formState.type}`} role="status">
                  {formState.message}
                </p>
              )}
            </form>
          </aside>
        </section>

        <section className="detail-section" id="overview">
          <div className="detail-split">
            <div className="detail-panel">
              <p className="detail-eyebrow">Overview</p>
              <h2>{property.title}</h2>
              {descriptionHtml ? (
                <div
                  className="detail-rich-content"
                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                />
              ) : (
                <p>{summary}</p>
              )}
              <div className="detail-facts detail-facts-inline">
                {[
                  [property.community, 'Community'],
                  [property.city, 'City'],
                  [displayMetric(property.bathrooms), 'Bathrooms'],
                  [displayMetric(property.parking), 'Parking']
                ].filter(([value]) => value).map(([value, label]) => (
                  <div className="fact-card" key={label}><strong>{value}</strong><span>{label}</span></div>
                ))}
              </div>
            </div>
            <div className="detail-media">
              <button type="button" onClick={() => openLightbox(photos.length ? photos : [{ url: heroImage, name: altText }], 0)}>
                <img src={heroImage} alt={altText} />
              </button>
            </div>
          </div>
        </section>

        {amenities.length > 0 && (
          <section className="detail-section detail-parallax amenities-parallax" id="amenities">
            <div className="detail-section-header">
              <p className="detail-eyebrow">Property features</p>
              <h2>Amenities</h2>
            </div>
            <div className="amenity-grid">
              {amenities.map((amenity) => (
                <article className="amenity-card" key={amenity}>
                  <i className="fa-solid fa-check" aria-hidden="true" />
                  <div><h3>{amenity}</h3></div>
                </article>
              ))}
            </div>
          </section>
        )}

        {photos.length > 0 && (
          <section className="detail-section is-dark" id="gallery">
            <div className="detail-section-header">
              <p className="detail-eyebrow">Property images</p>
              <h2>Gallery</h2>
            </div>
            <div className="gallery-grid">
              {photos.slice(0, 9).map((photo, index) => (
                <button type="button" key={photo.id || photo.url} onClick={() => openLightbox(photos, index)}>
                  <img src={photo.url} alt={`${altText} ${index + 1}`} loading={index === 0 ? 'eager' : 'lazy'} />
                </button>
              ))}
            </div>
          </section>
        )}

        {paymentPlans.length > 0 && (
          <section className="detail-section is-dark detail-parallax payment-parallax" id="payment-plan">
            <div className="detail-section-header">
              <p className="detail-eyebrow">Payment plan</p>
              <h2>Flexible payment schedule</h2>
            </div>
            <div className="payment-timeline" style={{ gridTemplateColumns: `repeat(${Math.min(paymentPlans.length, 6)}, minmax(0, 1fr))` }}>
              {paymentPlans.map((plan, index) => (
                <div className="payment-step" key={`${plan.label}-${index}`}>
                  <strong>{plan.percentage ? `${plan.percentage}%` : '—'}</strong>
                  <span>{plan.label || `Installment ${index + 1}`}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {floorPlans.length > 0 && (
          <section className="detail-section" id="floor-plans">
            <div className="detail-section-header">
              <p className="detail-eyebrow">Layouts</p>
              <h2>Floor plans</h2>
            </div>
            <div className="floor-slider">
              <div className="floor-viewport">
                <div className="floor-grid" style={{ transform: `translate3d(${-floorIndex * 100}%, 0, 0)` }}>
                  {floorPlans.map((floor, index) => (
                    <article className="floor-card" key={floor.id || floor.url} aria-hidden={index !== floorIndex}>
                      <button className="floor-plan-link" type="button" onClick={() => openLightbox(floorPlans, index)}>
                        <img src={floor.url} alt={`${floor.name || property.title} floor plan`} loading="lazy" />
                        <span>View larger</span>
                      </button>
                      <h3>{floor.name || `Floor plan ${index + 1}`}</h3>
                    </article>
                  ))}
                </div>
              </div>
              {floorPlans.length > 1 && (
                <div className="floor-slider-controls" aria-label="Floor plan slider controls">
                  <button className="floor-slider-btn" type="button" aria-label="Previous floor plan" onClick={() => setFloorIndex((floorIndex - 1 + floorPlans.length) % floorPlans.length)}>
                    <i className="fa-solid fa-chevron-left" />
                  </button>
                  <div className="floor-slider-dots">
                    {floorPlans.map((floor, index) => (
                      <button
                        className={`floor-slider-dot ${index === floorIndex ? 'is-active' : ''}`}
                        type="button"
                        key={floor.id || index}
                        aria-label={`Show floor plan ${index + 1}`}
                        onClick={() => setFloorIndex(index)}
                      />
                    ))}
                  </div>
                  <button className="floor-slider-btn" type="button" aria-label="Next floor plan" onClick={() => setFloorIndex((floorIndex + 1) % floorPlans.length)}>
                    <i className="fa-solid fa-chevron-right" />
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        <section className="detail-section is-dark" id="location">
          <div className="location-panel">
            <p className="detail-eyebrow">Location</p>
            <h2>{[property.community, property.sub_community, property.city].filter(Boolean).join(', ')}</h2>
            <div className="location-grid">
              {[
                [property.developer, 'Developer'],
                [property.offering_type, 'Offering'],
                [property.furnished, 'Furnished'],
                [property.reference_number, 'Reference']
              ].filter(([value]) => value).map(([value, label]) => (
                <article className="location-card" key={label}><h3>{value}</h3><span>{label}</span></article>
              ))}
            </div>
          </div>
        </section>

        <section className="detail-section detail-final detail-parallax final-parallax" id="contact">
          <div className="detail-section-header">
            <p className="detail-eyebrow">Private property advisory</p>
            <h2>Request pricing, availability and full property details.</h2>
            <p>Our advisory team will help you select the right unit for your requirements.</p>
            <div className="detail-actions">
              <a className="detail-button is-filled" href="#register">Send enquiry</a>
              <Link className="detail-button" to="/new-projects">Back to properties</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {lightbox && (
        <div className="detail-react-lightbox" role="dialog" aria-modal="true" aria-label="Property image gallery">
          <div className="detail-lightbox-counter">{lightbox.index + 1} / {lightbox.items.length}</div>

          <div className="detail-lightbox-toolbar">
            <button type="button" aria-label="Share image" onClick={shareCurrentImage}>
              <i className="fa-solid fa-share-nodes" />
            </button>
            <button type="button" aria-label="Zoom out" onClick={() => setLightboxScale((scale) => Math.max(0.75, scale - 0.25))}>
              <i className="fa-solid fa-magnifying-glass-minus" />
            </button>
            <button type="button" aria-label="Zoom in" onClick={() => setLightboxScale((scale) => Math.min(2.5, scale + 0.25))}>
              <i className="fa-solid fa-magnifying-glass-plus" />
            </button>
            <button type="button" aria-label="Toggle fullscreen" onClick={toggleFullscreen}>
              <i className="fa-solid fa-expand" />
            </button>
            <button type="button" aria-label="Close image gallery" onClick={() => setLightbox(null)}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>

          {lightbox.items.length > 1 && (
            <>
              <button className="detail-lightbox-arrow is-previous" type="button" aria-label="Previous image" onClick={() => moveLightbox(-1)}>
                <i className="fa-solid fa-arrow-left" />
              </button>
              <button className="detail-lightbox-arrow is-next" type="button" aria-label="Next image" onClick={() => moveLightbox(1)}>
                <i className="fa-solid fa-arrow-right" />
              </button>
            </>
          )}

          <div className="detail-lightbox-preview-rail" aria-hidden="true">
            {lightbox.items.map((item, index) => (
              <button
                type="button"
                className={index === lightbox.index ? 'is-current' : ''}
                key={item.id || `${item.url}-${index}`}
                tabIndex="-1"
                onClick={() => {
                  setLightbox({ ...lightbox, index });
                  setLightboxScale(1);
                }}
              >
                <img src={item.url} alt="" />
              </button>
            ))}
          </div>

          <figure className="detail-lightbox-stage">
            <div className="detail-lightbox-image-wrap">
              <img
                src={lightbox.items[lightbox.index].url}
                alt={lightbox.items[lightbox.index].name || altText}
                style={{ transform: `scale(${lightboxScale})` }}
              />
            </div>
            <figcaption>{lightbox.items[lightbox.index].name || altText}</figcaption>
          </figure>
        </div>
      )}

      <a className="advisor-button" href="https://api.whatsapp.com/send?phone=971507135432&text=Hi" target="_blank" rel="noopener noreferrer" aria-label="Speak with an advisor on WhatsApp">
        <svg className="advisor-button__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M21 11.5a8.5 8.5 0 0 1-12.7 7.4L3 20l1.2-5A8.5 8.5 0 1 1 21 11.5Z" />
        </svg>
        <span>Speak with an advisor</span>
      </a>
    </>
  );
};

export default PropertyDetails;
